import { useState, useRef } from "react";

interface FileItem {
  id: string;
  name: string;
  label: string;
  required: boolean;
  status: "pending" | "uploading" | "done" | "error";
  fileName?: string;
  fileSize?: string;
  uploadTime?: string;
}

const REQUIRED_DOCS: FileItem[] = [
  { id: "id_card", name: "id_card", label: "居民身份证（正反面）", required: true, status: "done", fileName: "身份证_张建国.jpg", fileSize: "1.2MB", uploadTime: "2026-04-01 09:10" },
  { id: "property", name: "property", label: "房产证/购房合同", required: true, status: "done", fileName: "房产证_朝阳区建国路88号.pdf", fileSize: "3.5MB", uploadTime: "2026-04-01 09:12" },
  { id: "contract", name: "contract", label: "用气申请合同", required: true, status: "pending" },
  { id: "survey_report", name: "survey_report", label: "现场勘查报告", required: false, status: "pending" },
  { id: "construction_plan", name: "construction_plan", label: "施工方案图纸", required: false, status: "pending" },
  { id: "acceptance", name: "acceptance", label: "竣工验收报告", required: false, status: "pending" },
  { id: "safety_cert", name: "safety_cert", label: "安全检查证明", required: false, status: "pending" },
];

interface FileUploadPanelProps {
  onClose: () => void;
  appId: string;
}

export default function FileUploadPanel({ onClose, appId }: FileUploadPanelProps) {
  const [files, setFiles] = useState<FileItem[]>(REQUIRED_DOCS);
  const [dragging, setDragging] = useState<string | null>(null);
  const [uploading, setUploading] = useState<string | null>(null);
  const [previewFile, setPreviewFile] = useState<FileItem | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeUploadId, setActiveUploadId] = useState<string | null>(null);

  const handleUploadClick = (id: string) => {
    setActiveUploadId(id);
    fileInputRef.current?.click();
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !activeUploadId) return;
    simulateUpload(activeUploadId, file.name, `${(file.size / 1024 / 1024).toFixed(1)}MB`);
    e.target.value = "";
  };

  const simulateUpload = (id: string, fileName: string, fileSize: string) => {
    setUploading(id);
    setFiles(prev => prev.map(f => f.id === id ? { ...f, status: "uploading" } : f));
    setTimeout(() => {
      const now = new Date();
      const timeStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")} ${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
      setFiles(prev => prev.map(f => f.id === id ? {
        ...f,
        status: "done",
        fileName,
        fileSize,
        uploadTime: timeStr,
      } : f));
      setUploading(null);
    }, 1500);
  };

  const handleDrop = (e: React.DragEvent, id: string) => {
    e.preventDefault();
    setDragging(null);
    const file = e.dataTransfer.files?.[0];
    if (file) {
      simulateUpload(id, file.name, `${(file.size / 1024 / 1024).toFixed(1)}MB`);
    }
  };

  const handleDelete = (id: string) => {
    setFiles(prev => prev.map(f => f.id === id ? { ...f, status: "pending", fileName: undefined, fileSize: undefined, uploadTime: undefined } : f));
  };

  const doneCount = files.filter(f => f.status === "done").length;
  const requiredDone = files.filter(f => f.required && f.status === "done").length;
  const requiredTotal = files.filter(f => f.required).length;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={onClose}>
      <div className="bg-white rounded-2xl w-[680px] max-h-[88vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-base font-bold text-gray-900">业务文件管理</h3>
            <p className="text-xs text-gray-400 mt-0.5">申请编号：{appId} · 支持 JPG/PNG/PDF/Word 格式</p>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2 text-xs">
              <span className="text-gray-500">必填资料：</span>
              <span className={`font-bold ${requiredDone === requiredTotal ? "text-green-600" : "text-amber-600"}`}>
                {requiredDone}/{requiredTotal}
              </span>
              <span className="text-gray-400">已上传</span>
            </div>
            <button onClick={onClose} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer">
              <i className="ri-close-line text-gray-500"></i>
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-100">
          <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
            <span>上传进度</span>
            <span>{doneCount}/{files.length} 份文件已上传</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div
              className="h-2 rounded-full transition-all duration-500"
              style={{
                width: `${(doneCount / files.length) * 100}%`,
                background: doneCount === files.length ? "#22c55e" : "#E8531A"
              }}
            />
          </div>
        </div>

        {/* File list */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
          {files.map(file => (
            <div
              key={file.id}
              className={`rounded-xl border-2 transition-all ${
                dragging === file.id ? "border-[#E8531A] bg-orange-50" :
                file.status === "done" ? "border-green-200 bg-green-50/50" :
                file.status === "uploading" ? "border-amber-200 bg-amber-50/50" :
                "border-gray-100 bg-white hover:border-gray-200"
              }`}
              onDragOver={e => { e.preventDefault(); setDragging(file.id); }}
              onDragLeave={() => setDragging(null)}
              onDrop={e => handleDrop(e, file.id)}
            >
              <div className="p-4">
                <div className="flex items-start justify-between">
                  <div className="flex items-start gap-3 flex-1">
                    {/* File type icon */}
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                      file.status === "done" ? "bg-green-100" :
                      file.status === "uploading" ? "bg-amber-100" :
                      "bg-gray-100"
                    }`}>
                      {file.status === "uploading" ? (
                        <i className="ri-loader-4-line text-amber-500 animate-spin text-lg"></i>
                      ) : file.status === "done" ? (
                        <i className="ri-file-check-line text-green-500 text-lg"></i>
                      ) : (
                        <i className="ri-file-upload-line text-gray-400 text-lg"></i>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="text-sm font-medium text-gray-800">{file.label}</span>
                        {file.required && (
                          <span className="text-[10px] text-red-500 bg-red-50 px-1.5 py-0.5 rounded-full">必填</span>
                        )}
                        {!file.required && (
                          <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded-full">选填</span>
                        )}
                      </div>

                      {file.status === "done" && file.fileName ? (
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          <span className="text-green-600 font-medium truncate max-w-[200px]">{file.fileName}</span>
                          <span>{file.fileSize}</span>
                          <span>{file.uploadTime}</span>
                        </div>
                      ) : file.status === "uploading" ? (
                        <div className="text-xs text-amber-600">正在上传中...</div>
                      ) : (
                        <div className="text-xs text-gray-400">
                          拖拽文件到此处，或点击右侧按钮上传
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex items-center gap-2 ml-3 flex-shrink-0">
                    {file.status === "done" ? (
                      <>
                        <button
                          onClick={() => setPreviewFile(file)}
                          className="text-xs text-[#E8531A] border border-[#E8531A]/30 px-2.5 py-1.5 rounded-lg cursor-pointer hover:bg-orange-50 whitespace-nowrap"
                        >
                          <i className="ri-eye-line mr-1"></i>预览
                        </button>
                        <button
                          onClick={() => handleDelete(file.id)}
                          className="text-xs text-gray-400 border border-gray-200 px-2.5 py-1.5 rounded-lg cursor-pointer hover:bg-gray-50 whitespace-nowrap"
                        >
                          <i className="ri-delete-bin-line mr-1"></i>删除
                        </button>
                      </>
                    ) : file.status === "uploading" ? (
                      <div className="text-xs text-amber-500 px-3 py-1.5">上传中...</div>
                    ) : (
                      <button
                        onClick={() => handleUploadClick(file.id)}
                        className="text-xs bg-[#E8531A] text-white px-3 py-1.5 rounded-lg cursor-pointer hover:bg-[#d44a15] whitespace-nowrap"
                      >
                        <i className="ri-upload-line mr-1"></i>上传文件
                      </button>
                    )}
                  </div>
                </div>

                {/* Upload progress bar */}
                {file.status === "uploading" && (
                  <div className="mt-3">
                    <div className="w-full bg-amber-100 rounded-full h-1.5">
                      <div className="h-1.5 rounded-full bg-amber-500 animate-pulse" style={{ width: "60%" }} />
                    </div>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between bg-gray-50">
          <div className="text-xs text-gray-400">
            <i className="ri-information-line mr-1"></i>
            文件大小限制：单个文件不超过20MB，支持 JPG/PNG/PDF/DOC/DOCX 格式
          </div>
          <div className="flex gap-2">
            <button onClick={onClose} className="px-4 py-2 border border-gray-200 text-gray-600 rounded-lg text-sm cursor-pointer hover:bg-gray-100 whitespace-nowrap">
              关闭
            </button>
            <button
              className={`px-4 py-2 rounded-lg text-sm cursor-pointer whitespace-nowrap ${
                requiredDone === requiredTotal
                  ? "bg-green-500 text-white hover:bg-green-600"
                  : "bg-[#E8531A] text-white hover:bg-[#d44a15]"
              }`}
            >
              {requiredDone === requiredTotal ? (
                <><i className="ri-check-double-line mr-1"></i>资料完整，提交审核</>
              ) : (
                <><i className="ri-save-line mr-1"></i>保存进度</>
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Hidden file input */}
      <input ref={fileInputRef} type="file" className="hidden" accept=".jpg,.jpeg,.png,.pdf,.doc,.docx" onChange={handleFileChange} />

      {/* Preview Modal */}
      {previewFile && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-60" onClick={() => setPreviewFile(null)}>
          <div className="bg-white rounded-2xl p-6 w-[480px]" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h4 className="text-sm font-bold text-gray-900">{previewFile.label}</h4>
              <button onClick={() => setPreviewFile(null)} className="w-7 h-7 flex items-center justify-center rounded-full hover:bg-gray-100 cursor-pointer">
                <i className="ri-close-line text-gray-500"></i>
              </button>
            </div>
            <div className="bg-gray-50 rounded-xl p-8 flex flex-col items-center justify-center gap-3 mb-4">
              <div className="w-16 h-16 rounded-2xl bg-green-100 flex items-center justify-center">
                <i className="ri-file-check-line text-green-500 text-3xl"></i>
              </div>
              <div className="text-center">
                <div className="text-sm font-medium text-gray-800">{previewFile.fileName}</div>
                <div className="text-xs text-gray-400 mt-1">{previewFile.fileSize} · 上传于 {previewFile.uploadTime}</div>
              </div>
            </div>
            <div className="flex gap-2">
              <button className="flex-1 border border-gray-200 text-gray-600 py-2 rounded-lg text-sm cursor-pointer hover:bg-gray-50 whitespace-nowrap">
                <i className="ri-download-line mr-1"></i>下载
              </button>
              <button className="flex-1 bg-[#E8531A] text-white py-2 rounded-lg text-sm cursor-pointer hover:bg-[#d44a15] whitespace-nowrap">
                <i className="ri-eye-line mr-1"></i>查看原文件
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
